/**
 * Hash Utility Module
 * 
 * This module provides hashing utilities for generating unique identifiers
 * for data in the application. These identifiers are used to track and
 * retrieve information associated with specific pairs of search terms.
 */

/**
 * generatePairHash: Create a consistent identifier for a pair of terms
 * 
 * This function generates a hash that uniquely identifies a pair of search terms,
 * regardless of the order they're provided in. This is crucial because:
 * 
 * 1. The same pair should always generate the same hash, even if the terms
 *    are provided in a different order
 * 2. Different pairs should generate different hashes to avoid collisions
 * 3. The hash should be compact and usable as a key in databases and objects
 * 
 * The implementation uses a common string hashing algorithm (DJB2) that:
 * - First sorts the terms alphabetically to ensure order-independence
 * - Joins them with a separator to create a single string
 * - Applies the hashing algorithm to generate a numeric hash
 * - Converts the hash to a hexadecimal string for readability
 * 
 * @param leftTerm - First term in the pair
 * @param rightTerm - Second term in the pair
 * @returns A hexadecimal string representing the unique hash for this pair
 */
export const generatePairHash = (leftTerm: string, rightTerm: string): string => {
  // Step 1: Sort terms alphabetically to ensure consistent ordering
  // This is important because hash("A","B") must equal hash("B","A")
  const terms = [leftTerm, rightTerm].sort().join('_');
  
  // Step 2: Initialize hash value
  let hash = 0;
  
  // Step 3: Apply DJB2 hashing algorithm to generate a numeric hash
  for (let i = 0; i < terms.length; i++) {
    const char = terms.charCodeAt(i);  // Get character code
    hash = ((hash << 5) - hash) + char; // hash * 31 + char
    hash = hash & hash; // Convert to 32-bit integer (bitwise AND with itself)
  }
  
  // Step 4: Convert to hexadecimal string for storage and display
  return hash.toString(16);
};